import React, { useState, useEffect, useCallback } from 'react';
import { Calendar } from 'lucide-react';
import { User, MessageSquare, RotateCcw, FileText, Download, X, History, Clock } from './icons';
import api, { asignacionesAPI } from '../services/api';
import { buildActaHistoricaUrl } from '../config';
import GenerarActaModal from './GenerarActaModal';

const HistorialAsignacionesContent = ({ activo }) => {
    const [asignaciones, setAsignaciones] = useState([]);
    const [actasHistoricas, setActasHistoricas] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showGenerarActaModal, setShowGenerarActaModal] = useState(false);
    const [selectedAsignacion, setSelectedAsignacion] = useState(null);

    const fetchAsignaciones = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const { data } = await asignacionesAPI.getByActivo(activo.id);
            setAsignaciones(data);
        } catch (err) {
            console.error('Error al cargar asignaciones:', err);
            setError('Error al cargar el historial de asignaciones');
        } finally {
            setLoading(false);
        }
    }, [activo]);

    const fetchActasHistoricas = useCallback(async () => {
        try {
            const { data } = await api.get(`/actas/activo/${activo.id}`);
            setActasHistoricas(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Error al cargar actas históricas:', err);
        }
    }, [activo]);

    useEffect(() => {
        if (activo) {
            fetchAsignaciones();
            fetchActasHistoricas();
        }
    }, [activo, fetchAsignaciones, fetchActasHistoricas]);

    const handleDevolver = async (asignacionId) => {
        if (!window.confirm('¿Estás seguro de que quieres devolver este activo?')) return;
        try {
            await asignacionesAPI.devolver(asignacionId, { observaciones: 'Devuelto por el usuario' });
            fetchAsignaciones();
        } catch (err) {
            console.error('Error al devolver activo:', err);
            setError('Error al devolver el activo');
        }
    };

    const handleQuitarAsignacion = async (asignacionId) => {
        if (!window.confirm('¿Estás seguro de que quieres eliminar completamente esta asignación? Esta acción no se puede deshacer.')) return;
        try {
            await asignacionesAPI.devolver(asignacionId, { observaciones: 'Asignación eliminada completamente' });
            fetchAsignaciones();
        } catch (err) {
            console.error('Error al eliminar asignación:', err);
            setError('Error al eliminar la asignación');
        }
    };

    const handleGenerarActa = (asignacion) => {
        setSelectedAsignacion(asignacion);
        setShowGenerarActaModal(true);
    };

    const handleCloseGenerarActa = () => {
        setShowGenerarActaModal(false);
        setSelectedAsignacion(null);
    };

    const handlePreviewHistorica = async (acta) => {
        try {
            const response = await api.get(`/actas/${acta.id}/descargar`, { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
            window.open(url, '_blank');
        } catch (err) {
            console.error('Error al previsualizar acta histórica:', err);
            // Fallback: intentar abrir directamente desde la ruta del archivo
            if (acta.rutaArchivo) {
                window.open(buildActaHistoricaUrl(acta.rutaArchivo), '_blank');
            }
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'Sin fecha';
        return new Date(dateStr).toLocaleDateString('es-ES', {
            year: 'numeric', month: 'long', day: 'numeric',
            hour: '2-digit', minute: '2-digit'
        });
    };

    if (!activo) return null;

    return (
        <>
            {error && (
                <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/20 border border-red-400 dark:border-red-600 text-red-700 dark:text-red-300 rounded-lg">
                    {error}
                </div>
            )}

            {loading && (
                <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
            )}

            {!loading && (
                <div className="space-y-4 max-h-[600px] overflow-y-auto">
                    {asignaciones.length === 0 && actasHistoricas.length === 0 ? (
                        <div className="text-center py-8">
                            <p className="text-gray-500 dark:text-gray-400">
                                No hay asignaciones ni actas históricas registradas para este activo.
                            </p>
                        </div>
                    ) : (
                        <>
                            {/* Asignaciones regulares */}
                            {asignaciones.map((asignacion) => {
                                const isActiva = asignacion.estado === 'Activa';
                                return (
                                    <div key={`asign-${asignacion.id}`} className={`border rounded-lg p-4 ${isActiva ? 'border-blue-100 dark:border-blue-900/30' : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/40 opacity-80'}`}>
                                        <div className="flex items-center justify-between mb-3">
                                            <div className="flex items-center space-x-3">
                                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isActiva ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400' : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'}`}>
                                                    <User className="w-5 h-5" />
                                                </div>
                                                <div>
                                                    <h4 className={`text-sm font-medium ${isActiva ? 'text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-300'}`}>
                                                        {asignacion.usuario?.nombre} {asignacion.usuario?.apellido}
                                                    </h4>
                                                    <p className="text-xs text-gray-500 dark:text-gray-400">{asignacion.usuario?.email}</p>
                                                </div>
                                            </div>
                                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${isActiva
                                                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                                : 'bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800/50'
                                                }`}>
                                                {isActiva ? asignacion.estado : 'Retirada'}
                                            </span>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                                            <div>
                                                <div className="flex items-center space-x-2 mb-1">
                                                    <Calendar className="w-4 h-4 text-gray-500" />
                                                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Fecha de Asignación</span>
                                                </div>
                                                <p className="text-sm text-gray-900 dark:text-white">{formatDate(asignacion.fechaAsignacion)}</p>
                                            </div>
                                            {asignacion.fechaDevolucion && (
                                                <div>
                                                    <div className="flex items-center space-x-2 mb-1">
                                                        <RotateCcw className="w-4 h-4 text-gray-500" />
                                                        <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Fecha de Devolución</span>
                                                    </div>
                                                    <p className="text-sm text-gray-900 dark:text-white">{formatDate(asignacion.fechaDevolucion)}</p>
                                                </div>
                                            )}
                                        </div>

                                        {asignacion.observaciones && (
                                            <div className="mb-3">
                                                <div className="flex items-center space-x-2 mb-1">
                                                    <MessageSquare className="w-4 h-4 text-gray-500" />
                                                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Observaciones</span>
                                                </div>
                                                <p className="text-sm text-gray-900 dark:text-white">{asignacion.observaciones}</p>
                                            </div>
                                        )}

                                        <div className="flex items-center justify-between pt-3 border-t border-gray-200 dark:border-gray-700">
                                            <div className="flex space-x-2">
                                                {isActiva && (
                                                    <>
                                                        <button onClick={() => handleGenerarActa(asignacion)} className="inline-flex items-center px-3 py-1 text-xs font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300">
                                                            <Download className="w-3 h-3 mr-1" /> Generar Acta
                                                        </button>
                                                        <button onClick={() => handleDevolver(asignacion.id)} className="inline-flex items-center px-3 py-1 text-xs font-medium text-yellow-600 hover:text-yellow-700 dark:text-yellow-400 dark:hover:text-yellow-300">
                                                            <RotateCcw className="w-3 h-3 mr-1" /> Devolver
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                            <button onClick={() => handleQuitarAsignacion(asignacion.id)} className="inline-flex items-center px-3 py-1 text-xs font-medium text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400 transition-colors">
                                                <X className="w-3 h-3 mr-1" /> Eliminar
                                            </button>
                                        </div>
                                    </div>
                                )
                            })}

                            {/* Separador + Actas Históricas */}
                            {actasHistoricas.length > 0 && (
                                <>
                                    {asignaciones.length > 0 && (
                                        <div className="flex items-center my-4">
                                            <div className="flex-grow border-t border-gray-300 dark:border-gray-600"></div>
                                            <span className="flex-shrink mx-3 text-xs font-semibold text-amber-600 dark:text-amber-400 uppercase tracking-wider flex items-center">
                                                <History className="w-3.5 h-3.5 mr-1.5" />
                                                Actas Históricas
                                            </span>
                                            <div className="flex-grow border-t border-gray-300 dark:border-gray-600"></div>
                                        </div>
                                    )}

                                    {actasHistoricas.map((acta) => (
                                        <div key={`hist-${acta.id}`} className="border border-amber-200 dark:border-amber-700/50 rounded-lg p-4 bg-amber-50/50 dark:bg-amber-900/10">
                                            <div className="flex items-center justify-between mb-3">
                                                <div className="flex items-center space-x-3">
                                                    <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/30 rounded-lg flex items-center justify-center">
                                                        <FileText className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                                                    </div>
                                                    <div>
                                                        <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                                                            {acta.usuarioNombreCompleto}
                                                        </h4>
                                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                                            {acta.nombreArchivo || 'Acta histórica'}
                                                        </p>
                                                    </div>
                                                </div>
                                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                                                    <Clock className="w-3 h-3 mr-1" />
                                                    Histórica
                                                </span>
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                                                <div>
                                                    <div className="flex items-center space-x-2 mb-1">
                                                        <Calendar className="w-4 h-4 text-gray-500" />
                                                        <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Fecha de Firma</span>
                                                    </div>
                                                    <p className="text-sm text-gray-900 dark:text-white">{formatDate(acta.fechaFirma)}</p>
                                                </div>
                                                <div>
                                                    <div className="flex items-center space-x-2 mb-1">
                                                        <Calendar className="w-4 h-4 text-gray-500" />
                                                        <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Fecha de Subida</span>
                                                    </div>
                                                    <p className="text-sm text-gray-900 dark:text-white">{formatDate(acta.fechaCreacion)}</p>
                                                </div>
                                            </div>

                                            <div className="flex items-center pt-3 border-t border-amber-200 dark:border-amber-700/50">
                                                <button
                                                    onClick={() => handlePreviewHistorica(acta)}
                                                    className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-amber-700 hover:text-amber-800 dark:text-amber-400 dark:hover:text-amber-300 bg-amber-100 dark:bg-amber-900/30 rounded-md hover:bg-amber-200 dark:hover:bg-amber-900/50 transition-colors"
                                                >
                                                    <Download className="w-3.5 h-3.5 mr-1.5" />
                                                    Previsualizar
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </>
                            )}
                        </>
                    )}
                </div>
            )}

            <GenerarActaModal
                isOpen={showGenerarActaModal}
                onClose={handleCloseGenerarActa}
                asignacion={selectedAsignacion}
            />
        </>
    );
};

export default HistorialAsignacionesContent;
